import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import { AlertTriangle, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Context for simulation status
const SimulationContext = createContext({
  isSimulationActive: false,
  simulatedProviders: [],
  refreshStatus: () => {}
});

export const useSimulation = () => useContext(SimulationContext);

export const SimulationProvider = ({ children }) => {
  const [isSimulationActive, setIsSimulationActive] = useState(false);
  const [simulatedProviders, setSimulatedProviders] = useState([]);

  const refreshStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/api/simulation/status`, { headers });
      
      setIsSimulationActive(response.data.simulation_active || false);
      setSimulatedProviders(response.data.simulated_providers || []);
    } catch (error) {
      // Silently fail - user might not be logged in or not admin
      setIsSimulationActive(false);
    }
  };

  useEffect(() => {
    refreshStatus();
    
    // Refresh every 30 seconds
    const interval = setInterval(refreshStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <SimulationContext.Provider value={{ isSimulationActive, simulatedProviders, refreshStatus }}>
      {children}
    </SimulationContext.Provider>
  );
};

// Banner component to show when simulation is active
export const SimulationBanner = () => {
  const { isSimulationActive, simulatedProviders } = useSimulation();
  const [dismissed, setDismissed] = useState(false);

  if (!isSimulationActive || dismissed) return null;

  return (
    <div 
      className="bg-amber-500 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 flex items-center justify-between"
      data-testid="simulation-banner"
    >
      <div className="flex items-center gap-3">
        <AlertTriangle className="w-5 h-5" />
        <span className="font-medium">Simulation Mode Active</span>
        <span className="text-amber-100 text-sm">
          Showing simulated data for: {simulatedProviders.map(p => p.toUpperCase()).join(', ')}
        </span>
      </div>
      <button 
        onClick={() => setDismissed(true)}
        className="p-1 hover:bg-white/20 rounded transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default SimulationBanner;
